/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, ReactNode, useEffect, useReducer } from 'react';

import { AvailableSearchSort, generateUrl } from './SearchHelpers';
import searchReducer from 'contexts/SearchReducer';
import Area from 'models/Area';
import Poi from 'models/Poi';
import Category from 'models/Category';
import { SearchFilter, SearchSort } from 'contexts/SearchHelpers';
import { ApiListResponse } from 'data/types';
import { SearchAction, setArea, setCategory, setUrl } from 'contexts/SearchActions';

export interface SearchState {
    initialised: boolean;
    isLoading: boolean;
    perPage: number;
    currentPage: number;
    results?: ApiListResponse<Poi>;
    url: string | null;
    category?: Category | null;
    categories: Category[];
    area?: Area | null;
    sort: SearchSort;
    sortDirection: string;
    filters: SearchFilter[];
    availableFilters: SearchFilter[];
    availableSorts: AvailableSearchSort[];
    mapView: boolean;
    mapResults: Poi[];
}

export const initialSearchState: SearchState = {
    initialised: false,
    isLoading: false,
    perPage: 24,
    currentPage: 1,
    results: null,
    url: null,
    category: null,
    categories: [],
    area: null,
    sort: null,
    sortDirection: 'ASC',
    filters: [],
    availableFilters: [],
    availableSorts: [],
    mapView: false,
    mapResults: []
};

export interface SearchAppContext {
    state: SearchState;
    dispatch: React.Dispatch<SearchAction>;
}

const SearchContext = createContext<SearchAppContext>({
    state: initialSearchState,
    dispatch: () => null
});

interface SearchProviderProps {
    results?: Poi[];
    area?: Area;
    category?: Category;
    categories: Category[];
    children: ReactNode;
}

export const SearchProvider = ({ area, category, categories, children }: SearchProviderProps) => {
    const initState: SearchState = {
        ...initialSearchState,
        isLoading: false,
        categories,
        area,
        category
    };

    const [state, dispatch] = useReducer(searchReducer, initState as SearchState);
    const value = { state, dispatch };

    const parametersToObserve = {
        currentPage: state.currentPage,
        area: state.area,
        category: state.category,
        sort: state.sort,
        sortDirection: state.sortDirection,
        filters: state.filters,
        mapView: state.mapView,
        initialised: state.initialised
    };

    useEffect(() => {
        if (state.initialised) {
            setUrl(dispatch, generateUrl({ ...parametersToObserve }));
        }
    }, Object.values(parametersToObserve));

    useEffect(() => {
        if (area) {
            setArea(dispatch, area);
        }
        if (category) {
            setCategory(dispatch, category);
        }
    }, [area, category]);

    return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export default SearchContext;
